import momentTz from 'moment-timezone';
import moment from 'moment';

function getActionType(action: any): string {
    const minValue = parseInt(action?.min);
    const maxValue = parseInt(action?.max ?? 0);
    const range = maxValue - minValue;

    if (range > 0) return 'slider';
    if (action?.dataValue?.length) return 'dropdown';
    if (range === 0 && action?.value) return 'textfield';
    return 'command';
}

const convertUtcToTimezoneTimestamp = (utcTime: number, timezone: string): number => {
    const zonedMoment = momentTz(utcTime).tz(timezone);
    const formatted = zonedMoment.format('MM/DD/YYYY HH:mm');
    const finalTimestamp = moment(formatted, 'MM/DD/YYYY HH:mm').tz(momentTz.tz.guess()).valueOf();
    return finalTimestamp;
};

function mapExcludeHolidays(payload: any, newPayload: any): void {
    if (payload?.exceptionHolidayCountry) {
        newPayload['selectedCountry'] = newPayload['exceptionHolidayCountry'];
        newPayload['exceptionType'] = 'holiday';
    } else if (
        payload?.excludeHolidays?.length &&
        payload?.excludeHolidays?.some((item: any) => typeof item !== 'object')
    ) {
        newPayload['excludeHolidays'] = payload.excludeHolidays?.map((item: any) => ({
            type: 'dateSelection',
            date: moment(item)?.toISOString(),
            label: moment(item)?.format('ll'),
        }));
    }
}

function assignActionTimes(payload: any, newPayload: any): void {
    const converted = convertUtcToTimezoneTimestamp(payload['actionStartTime'], newPayload?.timezone);
    const iso = new Date(converted)?.toISOString();
    newPayload['actionStartTime'] = iso;
    newPayload['startTime'] = iso;
}

function processScheduleKey(key: string, newPayload: any, payload: any): void {
    switch (key) {
        case 'endDate':
            if (newPayload[key]) {
                newPayload['repeat'] = true;
            }
            break;
        case 'repeatOccurence':
            if (newPayload[key]) {
                newPayload['repeatUntil'] = true;
            }
            break;
        case 'actionEndTime':
            if (newPayload[key]) {
                newPayload['isActionEndTimeRequired'] = true;
                newPayload['actionEndTime'] = new Date(
                    convertUtcToTimezoneTimestamp(payload['actionEndTime'], newPayload?.timezone)
                )?.toISOString();
                newPayload['endTime'] = new Date(
                    convertUtcToTimezoneTimestamp(payload['actionEndTime'], newPayload?.timezone)
                )?.toISOString();
            }
            break;
        default:
            break;
    }
}

function normalizeScheduleAssignedIds(newPayload: any): void {
    const getItemId = (item: any): any => item?.id ?? item;
    const groupIds = newPayload['groupIds'];
    if (!groupIds?.length) {
        newPayload['isDeviceSelectable'] = true;
    }
    newPayload['groupIds'] = groupIds?.map(getItemId);
    newPayload['deviceIds'] = newPayload['deviceIds']?.map(getItemId);

    const loadGroupIds = newPayload['loadGroupIds']?.map(getItemId) ?? [];
    newPayload['loadGroupIds'] = loadGroupIds;
    if (loadGroupIds.length) {
        newPayload['assetCategory'] = 'loadGroups';
        newPayload['isDeviceSelectable'] = false;
    }
}

const PREDEFINED_SCHEDULE_KEYS_TO_DELETE = [
    'deviceTypeId',
    'lastRun',
    'schedules',
    'deviceCount',
    'isEnabled',
    'nextRun',
    'status',
    'actions',
];

function getPredefinedSchedule(payload: any): any {
    const newPayload = JSON.parse(JSON.stringify(payload));
    newPayload['startDate'] = new Date(newPayload['startDate']).toISOString();
    if (newPayload['endDate']) {
        newPayload['endDate'] = new Date(newPayload['endDate']).toISOString();
    }
    mapExcludeHolidays(payload, newPayload);
    assignActionTimes(payload, newPayload);
    normalizeScheduleAssignedIds(newPayload);
    deleteKeys(newPayload, PREDEFINED_SCHEDULE_KEYS_TO_DELETE);
    for (const key in newPayload) {
        processScheduleKey(key, newPayload, payload);
    }

    return newPayload;
}

const handleActionsPayload = (actions: any): any =>
    actions.map((item: any) => ({
        id: item?.key,
        startValue: item?.startValue ? parseInt(item?.startValue) : parseInt(item?.default),
        ...(item?.endValue && {
            endValue: item?.endValue ? parseInt(item?.endValue) : parseInt(item?.default),
        }),
    }));

const assignIfArrayHasItems = (target: any, key: string, value: any): void => {
    if (value?.length) {
        target[key] = value;
    }
};

const applyLoadGroupScheduleAssignments = (cleanedPayload: any, payload: any): void => {
    cleanedPayload.loadGroupIds = payload.loadGroupIds || [];
    cleanedPayload.deviceTypeCategory = payload.deviceTypeId;
};

const applyDeviceScheduleAssignments = (cleanedPayload: any, payload: any): void => {
    cleanedPayload.deviceTypeId = payload.deviceTypeId;
    cleanedPayload.deviceTypeCategory = payload.deviceTypeCategory;
    assignIfArrayHasItems(cleanedPayload, 'deviceIds', payload?.deviceIds);
    assignIfArrayHasItems(cleanedPayload, 'groupIds', payload?.groupIds);
};

const applyAssetCategoryAssignments = (cleanedPayload: any, payload: any): void => {
    const assetCategoryHandlers: Record<string, () => void> = {
        loadGroups: (): void => applyLoadGroupScheduleAssignments(cleanedPayload, payload),
        default: (): void => applyDeviceScheduleAssignments(cleanedPayload, payload),
    };

    const handler = assetCategoryHandlers[payload.assetCategory] ?? assetCategoryHandlers.default;
    handler();
};

const generateCleanedSchedulePayload = (payload: any): any => {
    const cleanedPayload: any = {
        name: payload?.name,
        frequency: payload.frequency,
        startDate: new Date(payload.startDate)?.getTime(),
        endDate: new Date(payload.endDate)?.getTime(),
        actionStartTime: new Date(payload.startTime)?.getTime(),
        repeatForever: payload.repeatForever,
        repeatOccurrence: payload.repeatOccurrence,
        repeatOnDay: payload.repeatOnDay,
        repeatMonthDay: new Date(payload.repeatMonthDay)?.getTime(),
        repeatOn: payload.repeatOn,
        repeatEvery: payload.repeatEvery,
        timezone: payload.timezone,
        ...(payload['repeatOn'] === 'HOLIDAY' && { yearlyHolidayCountry: payload?.frequencyHolidayCountry }),
        ...(payload['exceptionType'] === 'holiday' && { exceptionHolidayCountry: payload?.selectedCountry }),
        ...(payload?.repeatEveryDays && { repeatEveryDays: payload.repeatEveryDays }),
        ...(payload?.repeatEveryWeek && { repeatEveryWeek: payload.repeatEveryWeek }),
        ...(payload?.repeatEveryMonth && { repeatEveryMonth: payload.repeatEveryMonth }),
        ...(payload?.isActionEndTimeRequired && { actionEndTime: new Date(payload.endTime)?.getTime() }),
        ...(payload?.excludeHolidays?.length &&
            payload?.excludeHolidays?.every((item: any) => typeof item === 'object') && {
                excludeHolidays: payload.excludeHolidays?.map((item: any) => new Date(item?.date)?.getTime()),
            }),
        ...(payload?.holidays?.length &&
            payload?.excludeHolidays?.every((item: any) => typeof item === 'object') && {
                holidays: payload.holidays?.map((item: any) => new Date(item?.date)?.getTime()),
            }),
        ...(payload?.weekDay?.length && { weekDay: payload.weekDay }),
        ...(payload?.monthList?.length && { monthList: payload.monthList }),
    };

    applyAssetCategoryAssignments(cleanedPayload, payload);

    return cleanedPayload;
};

const deleteKeys = (target: any, keys: string[]): void => {
    keys.forEach((key) => {
        delete target[key];
    });
};

const handleCleanFrequencyFromPayload = (payload: any, newPayload: any, key: string): void => {
    switch (payload[key]) {
        case 'ONCE':
            deleteKeys(newPayload, [
                'endDate',
                'weekDay',
                'repeatOn',
                'monthList',
                'repeatOnDay',
                'repeatEvery',
                'holidayList',
                'repeatForever',
                'repeatMonthDay',
                'repeatEveryDays',
                'repeatEveryWeek',
                'repeatEveryMonth',
                'repeatOccurrence',
            ]);
            break;
        case 'DAILY':
            deleteKeys(newPayload, [
                'repeatOn',
                'weekDay',
                'monthList',
                'repeatOnDay',
                'repeatEvery',
                'holidayList',
                'repeatMonthDay',
                'repeatEveryWeek',
                'repeatEveryMonth',
            ]);
            break;

        case 'WEEKLY':
            deleteKeys(newPayload, [
                'repeatOn',
                'repeatEveryDays',
                'repeatEveryMonth',
                'monthList',
                'repeatOnDay',
                'repeatEvery',
                'holidayList',
                'repeatMonthDay',
            ]);
            break;

        case 'MONTHLY':
            if (payload['repeatDay']) {
                deleteKeys(newPayload, ['weekDay', 'repeatEvery']);
            } else if (payload['repeatEvery']) {
                delete newPayload['repeatOnDay'];
            }
            deleteKeys(newPayload, ['repeatEveryDays', 'repeatEveryWeek', 'holidayList', 'repeatOn', 'repeatMonthDay']);
            break;
        case 'YEARLY':
            if (payload['repeatDay']) {
                deleteKeys(newPayload, ['weekDay', 'monthList', 'repeatEvery']);
            } else if (payload['repeatEvery']) {
                deleteKeys(newPayload, ['repeatOn', 'holidays', 'repeatMonthDay']);
            }
            deleteKeys(newPayload, ['repeatOnDay', 'repeatEveryDays', 'repeatEveryWeek', 'repeatEveryMonth']);
            break;
        default:
            break;
    }
};

function constructSchedulePayload(payload: any): any {
    const newPayload = generateCleanedSchedulePayload(payload);

    const shouldClearEndDateOnRepeat = payload?.repeat === true;
    const shouldClearOccurrenceOnRepeatUntil = payload?.repeatUntil === true;
    const shouldClearBothForRepeatForever = payload?.repeatForever === true;

    for (const key in payload) {
        if (key === 'actions') {
            newPayload[key] = handleActionsPayload(payload[key]);
        }

        if (key === 'frequency') {
            handleCleanFrequencyFromPayload(payload, newPayload, key);
        }
    }

    if (shouldClearEndDateOnRepeat || shouldClearBothForRepeatForever) {
        delete newPayload['endDate'];
    }
    if (shouldClearOccurrenceOnRepeatUntil || shouldClearBothForRepeatForever) {
        delete newPayload['repeatOccurrence'];
    }

    return newPayload;
}

function getFromToFormat(type: any, selectedDate: any, selectedTimezone: string): { from: number; to: number } {
    const localDate = momentTz.tz(moment(selectedDate).format('YYYY-MM-DD'), selectedTimezone);
    if (type === 'week') {
        const startOfWeek = localDate.clone().subtract(0, 'days').startOf('day');
        const endOfWeek = startOfWeek.clone().add(6, 'days').endOf('day');
        return {
            from: startOfWeek.valueOf(),
            to: endOfWeek.valueOf(),
        };
    }
    return {
        from: localDate.clone().startOf('day').valueOf(),
        to: localDate.clone().endOf('day').valueOf(),
    };
}

export { getActionType, constructSchedulePayload, getFromToFormat, getPredefinedSchedule };
